import React, { useEffect, useState, useContext, useRef } from 'react'
import classNames from 'classnames'
import { SelectInputProps } from './SelectInput.d'
import { usePageContext } from '@toasttab/shared/src/hooks/usePageContext'
import {
  FormikContext,
  FormikContextProps
} from '@toasttab/shared/src/hooks/useFormikContext'
import { getCountryNameByCountryCode } from '@toasttab/shared/src/utils/siteUtils'

const SelectInput2 = ({
  ariaEnabled,
  classes = '',
  disabled,
  error,
  fullWidth,
  id,
  label,
  multiple,
  name,
  onBlur,
  onChange,
  options = [{ disabled: false, label: '', value: '' }],
  padding = '',
  required,
  value,
  variation = 'default'
}: SelectInputProps) => {
  const [selectedOptions, setSelectedOptions] = useState([])
  const { region } = usePageContext()
  const formik: FormikContextProps = useContext(FormikContext) || null
  const ref = useRef(null)

  useEffect(() => {
    const event = new Event('change', { bubbles: true })
    ref.current.dispatchEvent(event)
    if (options.length > 0) {
      options.forEach((option: any, index: number) => {
        if (
          ref.current &&
          ref.current.value === '' &&
          !multiple &&
          region &&
          getCountryNameByCountryCode(region) === option.value
        ) {
          handleChange(index)
        }
      })
    }
  }, [formik?.formik?.values])

  const handleChange = (index: number) => {
    const event = new Event('change', { bubbles: true })
    ref.current.value = ref.current.options[index].value
    ref.current.dispatchEvent(event)
  }

  const selectStyles = classNames(classes, {
    // Base styles
    'bg-white border border-gray-50 appearance-none placeholder-text-secondary placeholder-tracking-[0.2px] leading-[24px] font-normal font-sansPro rounded-[4px]':
      true,
    'text-default': value,
    'text-secondary': value !== '',
    // Transitions
    'transition-colors transition-shadow duration-300': true,
    // Dropdown arrow, only on single select (not multiple)
    'bg-arrow-drop-down-dark bg-no-repeat bg-[center_right_1rem]': !multiple,
    // Full Width
    'w-full': fullWidth,
    // Arrow down
    // 'bg-arrow-drop-down-dark  ': true,
    // Error
    'border !border-error text-error': error,

    // Padding
    'py-3 px-[10px] h-[50px]': padding === 'large',
    'px-4 py-3 h-[48px]': padding === 'medium',
    'px-3 py-2': padding === ''
  })

  const errorClassNames = classNames({
    'absolute top-full w-max flex flex-row font-sansPro items-center text-error text-xs font-semibold left-0 leading-4 xl:-mb-[6px] before:content-[""] before:bg-icon-warning-outline before:bg-center before:bg-contain before:bg-no-repeat before:w-4 before:h-4 before:block before:mr-2':
      variation === 'default',
    'absolute top-full w-max flex flex-row font-sansPro items-center text-white text-xs font-semibold left-0 leading-4 xl:-mb-[6px] before:content-[""] before:bg-icon-warning-outline-inverse before:bg-center before:bg-contain before:bg-no-repeat before:w-4 before:h-4 before:block before:mr-2':
      variation === 'inverse'
    // 'top-12': padding === 'medium',
    // 'top-[56px]': padding === 'large'
  })

  return (
    <>
      <select
        className={selectStyles}
        data-testid='SelectInput2'
        disabled={disabled}
        id={id}
        multiple={multiple}
        name={name}
        onBlur={onBlur}
        onChange={onChange}
        ref={ref}
        required={required}
        value={multiple ? selectedOptions : selectedOptions[0]}
        {...(ariaEnabled
          ? {
              'aria-controls': options.map((option) => option.value).join(', ')
            }
          : {})}
      >
        {options.map(
          ({ disabled: optionDisabled, value, label: optionLabel }) => {
            const optionStyles = classNames({
              'text-secondary': optionDisabled
            })
            let selected = false
            if (formik?.formik?.values[name]) {
              selected = [value].includes(formik?.formik?.values[name])
            } else {
              selected = value === '' && !formik?.formik?.values[name]
            }
            return (
              <option
                className={optionStyles}
                data-testid='SelectInput2-option'
                disabled={optionDisabled}
                key={value}
                selected={selected}
                value={value}
                {...(ariaEnabled && ref.current.value === value
                  ? { 'aria-selected': true }
                  : {})}
              >
                {optionLabel}
              </option>
            )
          }
        )}
      </select>
      <label htmlFor={id} className='sr-only'>
        {label}
      </label>
      {error && (
        <div role='alert' className={errorClassNames}>
          {error}
        </div>
      )}
    </>
  )
}

export default SelectInput2
